import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link } from 'react-router-dom';
import { 
  Play, 
  Clock, 
  Users, 
  CheckCircle, 
  BookOpen, 
  Video, 
  FileText,
  BarChart3,
  Settings,
  Shield,
  Headphones,
  ArrowRight
} from 'lucide-react';

const ClientTraining = () => {
  const trainingModules = [
    {
      id: "content-management",
      title: "Content Management Basics",
      duration: "15 minutes",
      description: "Learn how to update blog posts, pages, and media on your website",
      topics: [
        "Adding and editing blog posts",
        "Updating page content", 
        "Managing images and media",
        "Using the WordPress editor",
        "Formatting text and links"
      ],
      videoUrl: "https://www.loom.com/embed/example1",
      difficulty: "Beginner"
    },
    {
      id: "image-management", 
      title: "Image & Media Management",
      duration: "12 minutes",
      description: "Master uploading, editing, and optimizing images for your website",
      topics: [
        "Uploading new images",
        "Resizing and cropping images",
        "Adding alt text for SEO",
        "Creating image galleries",
        "Managing media library"
      ],
      videoUrl: "https://www.loom.com/embed/example2", 
      difficulty: "Beginner"
    },
    {
      id: "analytics-basics",
      title: "Understanding Your Analytics",
      duration: "18 minutes", 
      description: "Learn to read and interpret your Google Analytics data",
      topics: [
        "Accessing Google Analytics",
        "Understanding key metrics",
        "Reading traffic reports",
        "Tracking conversions",
        "Setting up goals"
      ],
      videoUrl: "https://www.loom.com/embed/example3",
      difficulty: "Intermediate"
    }
  ];

  const supportResources = [
    {
      title: "Video Tutorial Library",
      description: "Access our complete collection of step-by-step video guides",
      icon: <Video className="h-6 w-6 text-accent" />,
      items: ["20+ Tutorial Videos", "Regular Updates", "Mobile Friendly"]
    },
    {
      title: "Written Documentation", 
      description: "Comprehensive guides and troubleshooting resources",
      icon: <FileText className="h-6 w-6 text-accent" />,
      items: ["Step-by-step Guides", "FAQ Section", "Troubleshooting Tips"]
    },
    {
      title: "Live Support Sessions",
      description: "Schedule one-on-one training sessions with our experts",
      icon: <Users className="h-6 w-6 text-accent" />,
      items: ["Screen Sharing", "Personalized Training", "Q&A Sessions"]
    },
    {
      title: "Priority Support",
      description: "Get help when you need it most with priority access",
      icon: <Headphones className="h-6 w-6 text-accent" />,
      items: ["24/7 Support", "Fast Response", "Expert Assistance"]
    }
  ];

  const benefits = [
    "Complete website independence",
    "Reduced support tickets and costs", 
    "Faster content updates",
    "Better understanding of your analytics",
    "Confidence in managing your site",
    "No more developer dependency"
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary/5 to-accent/5">
        <div className="container mx-auto px-4 text-center">
          <Badge className="bg-primary/10 text-primary border-primary/20 font-body mb-4">
            Client Empowerment
          </Badge>
          <h1 className="font-heading text-4xl md:text-6xl font-bold text-foreground mb-6">
            Take Complete Control of
            <span className="text-gradient"> Your Website</span>
          </h1>
          <p className="font-body text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Our comprehensive training program ensures you never feel helpless with your website again. 
            Learn everything you need to manage, update, and optimize your site independently.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg" 
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-semibold"
            >
              <Link to="/booking">
                Get Training Access
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button 
              asChild 
              variant="outline" 
              size="lg" 
              className="font-heading font-semibold"
            >
              <Link to="#training-modules">View Training Modules</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              Why Client Training Matters
            </h2>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              We believe in empowering our clients, not creating dependency. 
              Here's what you gain with our training program.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {benefits.map((benefit, index) => (
              <Card key={index} className="hover-lift shadow-card text-center">
                <CardContent className="p-6">
                  <CheckCircle className="h-8 w-8 text-accent mx-auto mb-3" />
                  <p className="font-body text-foreground font-medium">{benefit}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Card className="max-w-2xl mx-auto shadow-elegant bg-gradient-to-r from-accent/10 to-primary/10">
              <CardContent className="p-8">
                <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="font-heading text-xl font-bold text-foreground mb-3">
                  No More Developer Hostage Situations
                </h3>
                <p className="font-body text-muted-foreground">
                  Too many businesses are held hostage by their web developers. We break that cycle 
                  by giving you complete control and the knowledge to use it effectively.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Training Modules */}
      <section id="training-modules" className="section-padding bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              Training Modules
            </h2>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              Our bite-sized video tutorials make learning easy and practical. 
              Each module focuses on real tasks you'll need to perform.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {trainingModules.map((module, index) => (
              <Card key={module.id} className="hover-lift shadow-card">
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <Badge className="bg-accent/10 text-accent border-accent/20 font-body">
                      {module.difficulty}
                    </Badge>
                    <div className="flex items-center text-muted-foreground">
                      <Clock className="h-4 w-4 mr-1" />
                      <span className="font-body text-sm">{module.duration}</span>
                    </div>
                  </div>
                  <CardTitle className="font-heading text-lg font-bold text-foreground">
                    {module.title}
                  </CardTitle>
                  <p className="font-body text-sm text-muted-foreground">
                    {module.description}
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Video Placeholder */}
                  <div className="relative bg-muted rounded-lg aspect-video flex items-center justify-center">
                    <div className="text-center">
                      <Play className="h-12 w-12 text-primary mx-auto mb-2" />
                      <p className="font-body text-sm text-muted-foreground">
                        Video Tutorial Available
                      </p>
                    </div>
                  </div>

                  {/* Topics Covered */}
                  <div>
                    <h4 className="font-heading font-semibold text-foreground mb-2">
                      What You'll Learn:
                    </h4>
                    <ul className="space-y-1">
                      {module.topics.map((topic, topicIndex) => (
                        <li key={topicIndex} className="flex items-start space-x-2">
                          <CheckCircle className="h-3 w-3 text-accent mt-1 flex-shrink-0" />
                          <span className="font-body text-xs text-muted-foreground">{topic}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Button 
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-body"
                    disabled
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Watch Tutorial (Client Access Required)
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Support Resources */}
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              Ongoing Support Resources
            </h2>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              Training is just the beginning. We provide ongoing resources to ensure 
              your continued success and independence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {supportResources.map((resource, index) => (
              <Card key={index} className="hover-lift shadow-card">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
                        {resource.icon}
                      </div>
                    </div>
                    <div className="flex-grow">
                      <h3 className="font-heading text-lg font-bold text-foreground mb-2">
                        {resource.title}
                      </h3>
                      <p className="font-body text-muted-foreground mb-4">
                        {resource.description}
                      </p>
                      <div className="space-y-2">
                        {resource.items.map((item, itemIndex) => (
                          <div key={itemIndex} className="flex items-center space-x-2">
                            <CheckCircle className="h-3 w-3 text-accent" />
                            <span className="font-body text-sm text-foreground">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Training Process */}
      <section className="section-padding bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              How Our Training Works
            </h2>
            <p className="font-body text-lg text-primary-foreground/80 max-w-2xl mx-auto">
              We've designed our training process to be practical, efficient, and tailored to your specific needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="h-8 w-8 text-accent-foreground" />
              </div>
              <h3 className="font-heading text-lg font-semibold mb-2">1. Assessment</h3>
              <p className="font-body text-sm text-primary-foreground/80">
                We identify your specific training needs and skill level.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <Video className="h-8 w-8 text-accent-foreground" />
              </div>
              <h3 className="font-heading text-lg font-semibold mb-2">2. Custom Videos</h3>
              <p className="font-body text-sm text-primary-foreground/80">
                We create personalized video tutorials for your specific website.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-accent-foreground" />
              </div>
              <h3 className="font-heading text-lg font-semibold mb-2">3. Live Session</h3>
              <p className="font-body text-sm text-primary-foreground/80">
                One-on-one training session to practice and ask questions.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <Headphones className="h-8 w-8 text-accent-foreground" />
              </div>
              <h3 className="font-heading text-lg font-semibold mb-2">4. Ongoing Support</h3>
              <p className="font-body text-sm text-primary-foreground/80">
                Continued access to resources and support when needed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-accent/10 to-primary/10">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
              Ready to Take Control?
            </h2>
            <p className="font-body text-lg text-muted-foreground">
              Join hundreds of business owners who've gained complete independence over their websites. 
              No more waiting for developers, no more feeling helpless.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild 
                size="lg" 
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-semibold text-lg px-8 py-6"
              >
                <Link to="/booking">
                  Start Your Training Journey
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button 
                asChild 
                variant="outline" 
                size="lg" 
                className="font-heading font-semibold text-lg px-8 py-6"
              >
                <Link to="/services">View Our Services</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ClientTraining;